import { useContext, useEffect, useRef } from "react";
import { UNSAFE_NavigationContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useConfirmationModal } from "@/context/confirmationModalContext";
import { useRegisterUnsavedChanges } from "@/context/unsavedChangesContext";

export function useDocumentTemplateLeaveGuard(dirty: boolean) {
  const navigation = useContext(UNSAFE_NavigationContext);
  const { showConfirmationModalAsync } = useConfirmationModal();
  const confirmRef = useRef(showConfirmationModalAsync);
  confirmRef.current = showConfirmationModalAsync;
  const { t } = useTranslation();
  const message = t("unsaved_formChanges_confirm");
  useRegisterUnsavedChanges("document-template-editor", dirty);

  useEffect(() => {
    if (!dirty) return;
    const beforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    const navigator = navigation?.navigator;
    if (!navigator)
      return () => window.removeEventListener("beforeunload", beforeUnload);

    let mounted = true;
    let pending = false;
    let restoring = false;
    let index: number | undefined = window.history.state?.idx;
    let approvedIndex: number | undefined;
    const confirm = async (proceed: () => void) => {
      if (pending) return;
      pending = true;
      try {
        if ((await confirmRef.current(message)) && mounted) proceed();
      } finally {
        pending = false;
      }
    };
    const push = navigator.push;
    const replace = navigator.replace;
    const go = navigator.go;
    navigator.push = (...args) => {
      void confirm(() => {
        push.apply(navigator, args);
        index = window.history.state?.idx;
      });
    };
    navigator.replace = (...args) => {
      void confirm(() => {
        replace.apply(navigator, args);
        index = window.history.state?.idx;
      });
    };
    navigator.go = (...args) => {
      void confirm(() => {
        const currentIndex = window.history.state?.idx;
        approvedIndex =
          typeof currentIndex === "number" ? currentIndex + args[0] : undefined;
        go.apply(navigator, args);
      });
    };
    const pop = (event: PopStateEvent) => {
      const nextIndex: number | undefined = event.state?.idx;
      if (restoring) {
        restoring = false;
        event.stopImmediatePropagation();
        return;
      }
      if (approvedIndex !== undefined && approvedIndex === nextIndex) {
        approvedIndex = undefined;
        index = nextIndex;
        return;
      }
      approvedIndex = undefined;
      if (window.confirm(message)) {
        index = nextIndex;
        return;
      }
      if (
        typeof index === "number" &&
        typeof nextIndex === "number" &&
        index !== nextIndex
      ) {
        event.stopImmediatePropagation();
        restoring = true;
        window.history.go(index - nextIndex);
      }
    };
    window.addEventListener("popstate", pop, true); // BrowserRouter must not unmount the drafts before a canceled history change is restored.
    return () => {
      mounted = false;
      navigator.push = push;
      navigator.replace = replace;
      navigator.go = go;
      window.removeEventListener("popstate", pop, true);
      window.removeEventListener("beforeunload", beforeUnload);
    };
  }, [dirty, navigation?.navigator, message]);
}
